import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, Youtube, Twitter, DollarSign, TrendingDown, Filter, ArrowLeft, Search, X, Users, MapPin, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { ClubhouseNav } from '../components/clubhouse/ClubhouseNav';
import { TenureBadge } from '../components/clubhouse/TenureTracker';
import { clubhouseApi, logoutClubhouse } from '../utils/clubhouseApi';
import './ClubhouseMembers.css';

// Venmo SVG Icon component (more recognizable than DollarSign)
const VenmoIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.27 3c.94 1.56 1.36 3.16 1.36 5.2 0 5.77-4.92 13.27-8.92 18.55H4.86L2 4.08l6.16-.56 1.64 13.13c1.22-2 2.73-5.13 2.73-7.27 0-1.95-.33-3.27-.97-4.38L19.27 3z"/>
  </svg>
);

// Helper function to normalize Venmo URL
const getVenmoUrl = (venmoValue) => {
  if (!venmoValue) return null;
  // If already a URL, use as-is
  if (venmoValue.startsWith('http')) return venmoValue;
  // Otherwise, build URL from handle (remove @ if present)
  const handle = venmoValue.replace('@', '');
  return `https://venmo.com/u/${handle}`;
};

// Helper function to check if member's social links should be visible
const shouldShowSocialLinks = (member) => {
  // Check global toggle first (default to true for backwards compatibility)
  const privacy = member.privacy_settings || {};
  if (privacy.show_social_links === false) return false;
  return true;
};



export const ClubhouseMembers = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('handicap');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    hasHandicap: false,
    hasSocial: false,
    hasPhoto: false,
    handicapRange: 'all', // all, low (0-10), mid (10-20), high (20+)
    location: 'all',
  });

  useEffect(() => {
    if (!localStorage.getItem('clubhouse_user')) {
      navigate('/clubhouse/login');
      return;
    }

    fetchMembers();
    fetchUserProfile();
  }, [navigate, sortBy]);

  const fetchUserProfile = async () => {
    try {
      const response = await clubhouseApi.get(`/api/clubhouse/profile/me`);
      setUserProfile(response.data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await clubhouseApi.get(
        `/api/clubhouse/members/directory?sort=${sortBy}&limit=50`
      );
      setMembers(response.data);
      setFilteredMembers(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        await logoutClubhouse();
        navigate('/clubhouse/login');
      } else {
        setError('Failed to load members');
      }
    } finally {
      setLoading(false);
    }
  };

  // Apply all filters and search
  const applyFiltersAndSearch = () => {
    let filtered = [...members];

    // Apply search query
    if (searchQuery.trim()) {
      const lowercaseQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(member => {
        const name = member.first_name?.toLowerCase() || member.email.split('@')[0].toLowerCase();
        const email = member.email.toLowerCase();
        const bio = member.bio?.toLowerCase() || '';
        const location = member.shipping_address?.city?.toLowerCase() || '';
        const state = member.shipping_address?.state?.toLowerCase() || '';
        
        return name.includes(lowercaseQuery) || 
               email.includes(lowercaseQuery) || 
               bio.includes(lowercaseQuery) ||
               location.includes(lowercaseQuery) ||
               state.includes(lowercaseQuery);
      });
    }

    // Apply handicap filter
    if (filters.hasHandicap) {
      filtered = filtered.filter(member => member.current_handicap !== null);
    }

    // Apply handicap range filter
    if (filters.handicapRange !== 'all') {
      filtered = filtered.filter(member => {
        if (member.current_handicap === null) return false;
        const hdcp = member.current_handicap;
        switch (filters.handicapRange) {
          case 'low': return hdcp <= 10;
          case 'mid': return hdcp > 10 && hdcp <= 20;
          case 'high': return hdcp > 20;
          default: return true;
        }
      });
    }

    // Apply social media filter
    if (filters.hasSocial) {
      filtered = filtered.filter(member => 
        member.instagram_handle || member.youtube_url || member.x_handle
      );
    }

    // Apply photo filter
    if (filters.hasPhoto) {
      filtered = filtered.filter(member => member.profile_photo_url);
    }

    setFilteredMembers(filtered);
  };

  // Trigger filtering when dependencies change
  useEffect(() => {
    applyFiltersAndSearch();
  }, [searchQuery, filters, members]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const clearAllFilters = () => {
    setFilters({
      hasHandicap: false,
      hasSocial: false,
      hasPhoto: false,
      handicapRange: 'all',
      location: 'all',
    });
    setSearchQuery('');
  };

  const toggleFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const setHandicapRange = (range) => {
    setFilters(prev => ({
      ...prev,
      handicapRange: range
    }));
  };

  const activeFilterCount = Object.values(filters).filter(v => 
    typeof v === 'boolean' ? v : v !== 'all'
  ).length;

  const handleLogout = async () => {
    await logoutClubhouse();
    navigate('/clubhouse/login');
  };

  return (
    <div className="clubhouse-members">
      {/* Navigation */}
      <ClubhouseNav onLogout={handleLogout} userProfile={userProfile} />

      {/* Header */}
      <header className="members-header">
        <div className="container">
          <div className="header-content">
            <div className="header-title">
              <h1>Member Directory</h1>
              <p>Connect with fellow Golf Nuts from around the world</p>
            </div>
            
            {/* Search Bar */}
            <div className="header-search">
              <div className="search-input-wrapper">
                <Search size={20} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button onClick={clearSearch} className="clear-search">
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container members-container">
        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        {/* Filter Controls */}
        <div className="filter-controls">
          <div className="filter-header">
            <button 
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal size={18} />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="filter-count">{activeFilterCount}</span>
              )}
              <ChevronDown size={16} className={`chevron ${showFilters ? 'open' : ''}`} />
            </button>

            <div className="sort-and-count">
              <div className="sort-dropdown">
                <label>Sort:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
                  <option value="handicap">Lowest Handicap</option>
                  <option value="improved">Most Improved</option>
                  <option value="name">Name (A-Z)</option>
                  <option value="recent">Recently Joined</option>
                </select>
              </div>
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="filters-panel">
              <div className="filters-section">
                <h4 className="filter-section-title">Profile Filters</h4>
                <div className="filter-options">
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.hasPhoto}
                      onChange={() => toggleFilter('hasPhoto')}
                    />
                    <span>Has Profile Photo</span>
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.hasHandicap}
                      onChange={() => toggleFilter('hasHandicap')}
                    />
                    <span>Has Handicap</span>
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.hasSocial}
                      onChange={() => toggleFilter('hasSocial')}
                    />
                    <span>Has Social Media</span>
                  </label>
                </div>
              </div>

              <div className="filters-section">
                <h4 className="filter-section-title">Handicap Range</h4>
                <div className="filter-options horizontal">
                  <button
                    className={`filter-chip ${filters.handicapRange === 'all' ? 'active' : ''}`}
                    onClick={() => setHandicapRange('all')}
                  >
                    All
                  </button>
                  <button
                    className={`filter-chip ${filters.handicapRange === 'low' ? 'active' : ''}`}
                    onClick={() => setHandicapRange('low')}
                  >
                    0-10
                  </button>
                  <button
                    className={`filter-chip ${filters.handicapRange === 'mid' ? 'active' : ''}`}
                    onClick={() => setHandicapRange('mid')}
                  >
                    10-20
                  </button>
                  <button
                    className={`filter-chip ${filters.handicapRange === 'high' ? 'active' : ''}`}
                    onClick={() => setHandicapRange('high')}
                  >
                    20+
                  </button>
                </div>
              </div>

              {activeFilterCount > 0 && (
                <div className="filter-actions">
                  <button onClick={clearAllFilters} className="btn-clear-filters">
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Members Grid */}
        {filteredMembers.length === 0 ? (
          <div className="empty-state">
            {searchQuery ? (
              <>
                <Search size={48} className="empty-icon" />
                <p>No members found matching "{searchQuery}"</p>
                <button onClick={clearSearch} className="btn-secondary">
                  Clear Search
                </button>
              </>
            ) : (
              <p>No public member profiles yet. Be the first to make your profile public!</p>
            )}
          </div>
        ) : (
          <div className="members-grid">
            {filteredMembers.map((member) => (
              <MemberCard key={member.id} member={member} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MemberCard = ({ member }) => {
  return (
    <div className="member-card">
      {/* Profile Photo */}
      <div className="member-photo">
        {member.profile_photo_url ? (
          <img src={member.profile_photo_url} alt={member.first_name || 'Member'} />
        ) : (
          <div className="member-photo-placeholder">
            {(member.first_name || member.email)?.[0]?.toUpperCase()}
          </div>
        )}
      </div>

      {/* Member Info */}
      <div className="member-info">
        {member.display_preference === 'both' && member.username ? (
          <>
            <h3 className="member-name">{member.username}</h3>
            {(member.first_name || member.last_name) && (
              <p className="member-full-name-secondary">
                {member.first_name && member.last_name 
                  ? `${member.first_name} ${member.last_name}`
                  : member.first_name || ''}
              </p>
            )}
          </>
        ) : member.display_preference === 'username' && member.username ? (
          <h3 className="member-name">{member.username}</h3>
        ) : (
          <h3 className="member-name">
            {member.first_name && member.last_name 
              ? `${member.first_name} ${member.last_name}`
              : member.first_name || member.email.split('@')[0]}
          </h3>
        )}
        
        {member.bio && (
          <p className="member-bio">{member.bio}</p>
        )}

        {/* City/State - Only show if user has enabled it in privacy settings */}
        {member.privacy_settings?.show_city_state && 
         member.shipping_address?.city && 
         member.shipping_address?.state && (
          <div className="member-location">
            <MapPin size={14} />
            <span>{member.shipping_address.city}, {member.shipping_address.state}</span>
          </div>
        )}

        {/* Handicap Badge */}
        {member.current_handicap !== null && (
          <div className="handicap-badge">
            <TrendingDown size={14} />
            <span>{member.current_handicap.toFixed(1)} Index</span>
          </div>
        )}

        {/* Tenure Badge */}
        {member.created_at && (
          <div className="member-tenure">
            <TenureBadge createdAt={member.created_at} compact={true} />
          </div>
        )}

        {/* Social Links - respect privacy settings */}
        {shouldShowSocialLinks(member) && (member.instagram_handle || member.youtube_url || member.x_handle || member.venmo_username) && (
          <div className="member-social">
            {member.instagram_handle && (member.privacy_settings?.show_instagram !== false) && (
              <a 
                href={`https://instagram.com/${member.instagram_handle.replace('@', '')}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                title="Instagram"
              >
                <Instagram size={16} />
              </a>
            )}
            {member.youtube_url && (member.privacy_settings?.show_youtube !== false) && (
              <a 
                href={member.youtube_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                title="YouTube"
              >
                <Youtube size={16} />
              </a>
            )}
            {member.x_handle && (member.privacy_settings?.show_x !== false) && (
              <a 
                href={`https://x.com/${member.x_handle.replace('@', '')}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                title="X (Twitter)"
              >
                <Twitter size={16} />
              </a>
            )}
            {member.venmo_username && (member.privacy_settings?.show_venmo !== false) && (
              <a 
                href={getVenmoUrl(member.venmo_username)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                title="Venmo"
              >
                <VenmoIcon size={16} />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
